import { SkillDetail } from "@models";
import { Settings } from "@mui/icons-material";
import {
  Box,
  Divider,
  IconButton,
  Menu,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { SwitchButton } from "src/components/ui/SwitchButton";
import { isAdminUser } from "../../skill-utils";

type PermissionBearingUser = {
  permissions?: Array<string | undefined>;
};

type SkillSettingsMenuProps = {
  skill?: SkillDetail;
  user?: PermissionBearingUser | null;
  detailLoading: boolean;
  patchPending: boolean;
  closeKey: unknown;
  onRequestOpen: (open: () => void) => void;
  onToggleEnabled: (enabled: boolean) => void;
  onToggleForceEnabled: (forceEnabled: boolean) => void;
};

const SETTINGS_MENU_UNMOUNT_DELAY_MS = 100;

export const SkillSettingsMenu = ({
  skill,
  user,
  detailLoading,
  patchPending,
  closeKey,
  onRequestOpen,
  onToggleEnabled,
  onToggleForceEnabled,
}: SkillSettingsMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isMounted, setIsMounted] = useState(false);
  const unmountTimerRef = useRef<number | null>(null);
  const closeKeyRef = useRef(closeKey);
  const isOpen = Boolean(anchorEl);

  const canShowEnabled = !!skill;
  const canToggleForceEnabled =
    !!skill && skill.visibility === "FORMLY_INTERNAL" && isAdminUser(user);
  const hasSkillSettings = canShowEnabled || canToggleForceEnabled;
  const enabledDisabledReason = skill?.forceEnabled
    ? "This skill is Force Enabled system-wide, so it cannot be disabled."
    : undefined;

  const clearUnmountTimer = () => {
    if (unmountTimerRef.current === null) {
      return;
    }

    window.clearTimeout(unmountTimerRef.current);
    unmountTimerRef.current = null;
  };

  const closeMenu = () => {
    setAnchorEl(null);
    clearUnmountTimer();
    unmountTimerRef.current = window.setTimeout(() => {
      unmountTimerRef.current = null;
      setIsMounted(false);
    }, SETTINGS_MENU_UNMOUNT_DELAY_MS);
  };

  const openMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    const trigger = event.currentTarget;

    onRequestOpen(() => {
      clearUnmountTimer();
      setIsMounted(true);
      setAnchorEl(trigger);
    });
  };

  useEffect(
    () => () => {
      clearUnmountTimer();
    },
    []
  );

  useEffect(() => {
    if (closeKeyRef.current === closeKey) {
      return;
    }

    closeKeyRef.current = closeKey;
    if (isOpen || isMounted) {
      closeMenu();
    }
  }, [closeKey]);

  if (!skill || !hasSkillSettings) {
    return (
      <Tooltip title="Skill settings">
        <span>
          <IconButton
            aria-label="Skill settings"
            size="small"
            disabled={detailLoading || !skill || !hasSkillSettings}
          >
            <Settings fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    );
  }

  return (
    <>
      <Tooltip title="Skill settings">
        <IconButton
          id="skills-settings-button"
          aria-controls={isOpen ? "skills-settings-menu" : undefined}
          aria-expanded={isOpen ? "true" : undefined}
          aria-haspopup="menu"
          aria-label="Skill settings"
          size="small"
          onClick={openMenu}
          sx={{
            ...(isOpen
              ? {
                  backgroundColor: "rgba(7, 78, 232, 0.08)",
                  color: "primary.main",
                }
              : {}),
          }}
        >
          <Settings fontSize="small" />
        </IconButton>
      </Tooltip>

      {isMounted ? (
        <Menu
          id="skills-settings-menu"
          anchorEl={anchorEl}
          open={isOpen}
          onClose={closeMenu}
          autoFocus={false}
          transitionDuration={SETTINGS_MENU_UNMOUNT_DELAY_MS}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          MenuListProps={{
            "aria-labelledby": "skills-settings-button",
            sx: {
              paddingTop: "8px",
              paddingBottom: "4px",
            },
          }}
          slotProps={{
            root: {
              disableAutoFocus: true,
            },
            paper: {
              sx: {
                border: "1px solid rgba(148, 163, 184, 0.2)",
                borderRadius: "6px",
                boxShadow: "0 18px 50px rgba(15, 23, 42, 0.18)",
                maxHeight: "70vh",
                overflow: "hidden",
              },
            },
          }}
        >
          <Box
            sx={{
              boxSizing: "border-box",
              fontSize: "14px",
              pb: "4px",
            }}
          >
            {canShowEnabled ? (
              <SwitchButton
                label="Enabled"
                checked={skill.enabled}
                disabled={patchPending || skill.forceEnabled}
                disabledReason={enabledDisabledReason}
                info="Controls whether this visible skill is active for your runs."
                switchInputProps={{
                  id: "skills-enabled-switch",
                  "aria-label": "Enabled",
                }}
                onChange={onToggleEnabled}
              />
            ) : null}
            {canToggleForceEnabled ? (
              <>
                <Divider />
                <Typography
                  data-testid="skills-settings-danger-zone-title"
                  variant="body2"
                  sx={{
                    bgcolor: "background.paper",
                    color: "error.main",
                    fontSize: "14px",
                    fontWeight: 400,
                    letterSpacing: 0,
                    lineHeight: "24px",
                    m: "4px 6px",
                    p: "6px 10px",
                  }}
                >
                  Danger Zone
                </Typography>
                <Divider />
                <SwitchButton
                  label="Force Enabled System Wide"
                  checked={skill.forceEnabled}
                  disabled={patchPending}
                  info="This will enable the skill for every user in every organization and can materially alter the trajectory of an agent. Please use this with extreme care and only after confirming with Spencer and Severin"
                  switchInputProps={{
                    id: "skills-forceEnabled-switch",
                    "aria-label": "Force Enabled System Wide",
                  }}
                  onChange={onToggleForceEnabled}
                />
              </>
            ) : null}
          </Box>
        </Menu>
      ) : null}
    </>
  );
};
